import React from "react";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { QuestionDto } from "../../../services/dto";
import { useQueryClient } from "@tanstack/react-query";

import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { QuestionService } from "./questionService";
import { QuestionView } from "./questionView";
import { QuestionPage } from "./questionPage";

import { QuestionForm } from "./questionForm";
import { LoadingIndicator } from "../../../assets/vfx";

interface QuestionnaireQuestionContainerProps {
  questionnaireId?: string | undefined;
  questionnaireType?: string | undefined;
}

export const QuestionContainer = (
  props: QuestionnaireQuestionContainerProps
) => {
  const { questionnaireId } = props;
  const queryClient = useQueryClient();
  const [data, setData] = React.useState<QuestionDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const { showSnackbar } = useSnackbar();
  const questionQuery = QuestionService.getQuestionQuery(
    data?.id ?? Constants.emptyGuid
  );

  const lookupQuery = QuestionService.getLookups(questionnaireId);

  const { mutateAsync: saveQuestionnaireQuestion } = QuestionService.save();

  const { mutate: deleteQuestionnaireQuestion } =
    QuestionService.deleteQuestion();

  const handleSubmit = async (values: QuestionDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
    const message = isEdit ? "Question updated" : "Question saved";
    return await saveQuestionnaireQuestion(values, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar(message, MessageSeverity.Success);
          questionQuery.refetch();
          queryClient.invalidateQueries({
            queryKey: ["questionnaireQuestionGridQuery"],
          });
          closeDrawer();
        }
      },
    });
  };

  const handleDelete = () => {
    deleteQuestionnaireQuestion(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar("Question deleted", MessageSeverity.Success);
          questionQuery.refetch();
          queryClient.invalidateQueries({
            queryKey: ["questionnaireQuestionGridQuery"],
          });
          closeDrawer();
        }
      },
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string) => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as QuestionDto);
    }
  };

  const closeDrawer = () => setDrawerType(GRID_ACTION_TYPES.HIDDEN);

  const handleEdit = (data: QuestionDto) => {
    setData(data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
  };

  const renderDrawerContent = () => {
    const isDataLoaded = !!questionQuery?.data;

    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <QuestionForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery.data ?? undefined}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return isDataLoaded ? (
          <QuestionForm
            data={questionQuery.data}
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery.data}
          />
        ) : null;

      case GRID_ACTION_TYPES.VIEW:
        return isDataLoaded ? (
          <QuestionView
            data={questionQuery.data}
            show={true}
            onHide={closeDrawer}
            onDelete={handleDelete}
            lookups={lookupQuery.data}
            onEdit={() => handleEdit(questionQuery?.data)}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div>
      {lookupQuery?.isLoading ? (
        <LoadingIndicator isFullPage={true} />
      ) : (
        <QuestionPage
          onCustomEvent={handleCustomEvent}
          questionnaireId={questionnaireId}
          questionnaireType={props.questionnaireType}
          lookups={lookupQuery.data}
        />
      )}
      {renderDrawerContent()}
    </div>
  );
};
